import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ChevronLeft, Lock } from "lucide-react";
import { colors, type, spacing, radius } from "@/theme";
import { api } from "@/lib/api";
import { useCustomer } from "@/context/CustomerContext";

export default function CustomerTable() {
  const navigate = useNavigate();
  const { slug } = useParams();
  const { setSession, setSlug } = useCustomer();

  const [tenantName, setTenantName] = useState("");
  const [step, setStep] = useState("table");
  const [label, setLabel] = useState("");
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [generatedCode, setGeneratedCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (slug) setSlug(slug);
    api.getTenant(slug).then((t) => {
      setTenantName(t.nome);
      if (t?.ordinazione_abilitata === false) navigate(`/m/${slug}/menu`, { replace: true });
    }).catch(() => {});
    // eslint-disable-next-line
  }, [slug]);

  const onCheckTable = async () => {
    if (!label.trim()) return;
    setBusy(true); setError(null);
    try {
      const res = await api.checkTable(slug, label.trim());
      setLabel(res.etichetta);
      setStep(res.has_open_session ? "join" : "name");
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };

  const onOpen = async () => {
    if (!name.trim()) return;
    setBusy(true); setError(null);
    try {
      const res = await api.openSession(slug, label, name.trim());
      setGeneratedCode(res.codice);
      await setSession({ session_id: res.session_id, guest_id: res.guest_id, guest_name: res.guest_name, etichetta: res.etichetta, codice: res.codice });
      if (slug === "demo") { navigate(`/m/${slug}/menu`, { replace: true }); return; }
      setStep("code");
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };

  const onJoin = async () => {
    if (!name.trim() || code.length !== 4) return;
    setBusy(true); setError(null);
    try {
      const res = await api.joinSession(slug, label, code, name.trim());
      await setSession({ session_id: res.session_id, guest_id: res.guest_id, guest_name: res.guest_name, etichetta: res.etichetta, codice: code });
      navigate(`/m/${slug}/menu`, { replace: true });
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };

  return (
    <div style={{ minHeight: "100vh", background: colors.bg }}>
      <div style={{ maxWidth: 480, margin: "0 auto", padding: `${spacing.md}px ${spacing.lg}px ${spacing.xl}px`, minHeight: "100vh", display: "flex", flexDirection: "column" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: spacing.xxl }}>
          <button data-testid="back-button" onClick={() => (step === "table" ? navigate(-1) : setStep("table"))} style={iconBtn}><ChevronLeft size={26} color={colors.ink} /></button>
          <div style={{ ...type.sansSemi, fontSize: 15, color: colors.inkSoft }}>{tenantName}</div>
          <div style={{ width: 26 }} />
        </div>

        <div style={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "center", gap: spacing.md }}>
          {step === "table" && (
            <>
              <div style={big}>Che tavolo?</div>
              <div style={subtle}>{slug === "demo" ? "È un menu di esempio: scegli un tavolo da 1 a 12." : "Digita il numero che trovi sul tavolo."}</div>
              <input data-testid="table-label-input" value={label} onChange={(e) => setLabel(e.target.value)} placeholder="12" autoFocus maxLength={20} onKeyDown={(e) => e.key === "Enter" && onCheckTable()}
                style={{ ...type.serif, fontSize: 64, color: colors.ink, textAlign: "center", border: "none", borderBottom: `2px solid ${colors.line}`, background: "transparent", padding: `${spacing.md}px 0`, marginTop: spacing.md }} />
              {error ? <div style={errS}>{error}</div> : null}
              <button data-testid="table-continue-button" onClick={onCheckTable} disabled={busy || !label.trim()} style={{ ...cta, opacity: !label.trim() ? 0.4 : 1 }}>{busy ? "…" : "Continua"}</button>
            </>
          )}
          {step === "name" && (
            <>
              <div style={big}>Come ti chiami?</div>
              <div style={subtle}>Apri il tavolo {label}. Il nome serve in cucina.</div>
              <input data-testid="guest-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Il tuo nome" autoFocus maxLength={30} onKeyDown={(e) => e.key === "Enter" && onOpen()} style={textInput} />
              {error ? <div style={errS}>{error}</div> : null}
              <button data-testid="open-session-button" onClick={onOpen} disabled={busy || !name.trim()} style={{ ...cta, opacity: !name.trim() ? 0.4 : 1 }}>{busy ? "…" : "Apri il tavolo"}</button>
            </>
          )}
          {step === "join" && (
            <>
              <div style={big}>Unisciti al tavolo {label}</div>
              <div style={subtle}>C'è già qualcuno seduto qui. Fatti leggere il codice a 4 cifre.</div>
              <input data-testid="join-code-input" value={code} onChange={(e) => setCode(e.target.value.replace(/[^0-9]/g, "").slice(0, 4))} placeholder="0000" inputMode="numeric" autoFocus maxLength={4}
                style={{ ...type.serif, fontSize: 48, letterSpacing: 12, color: colors.ink, textAlign: "center", background: colors.surface, border: `1px solid ${colors.line}`, borderRadius: radius.md, padding: `${spacing.md}px 0`, marginTop: spacing.sm }} />
              <input data-testid="join-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Il tuo nome" maxLength={30} style={textInput} />
              {error ? <div style={errS}>{error}</div> : null}
              <button data-testid="join-session-button" onClick={onJoin} disabled={busy || code.length !== 4 || !name.trim()} style={{ ...cta, opacity: (code.length !== 4 || !name.trim()) ? 0.4 : 1 }}>{busy ? "…" : "Unisciti"}</button>
            </>
          )}
          {step === "code" && (
            <>
              <div style={{ ...type.sansSemi, fontSize: 15, color: colors.accent, textAlign: "center" }}>Tavolo {label} · il tuo codice</div>
              <div data-testid="generated-code" style={{ ...type.serif, fontSize: 96, letterSpacing: 8, color: colors.ink, textAlign: "center" }}>{generatedCode}</div>
              <div style={{ ...type.sans, fontSize: 16, color: colors.inkSoft, textAlign: "center", lineHeight: "24px" }}>Fallo digitare a chi è con te<br />per unirsi allo stesso conto.</div>
              <button data-testid="go-to-menu-button" onClick={() => navigate(`/m/${slug}/menu`, { replace: true })} style={cta}>Vai al menu</button>
            </>
          )}
        </div>

        {(step === "name" || step === "join") && (
          <button data-testid="privacy-link" onClick={() => navigate(`/m/${slug}/privacy`)} style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: spacing.xs, background: "none", border: "none", paddingTop: spacing.lg }}>
            <Lock size={13} color={colors.inkSoft} />
            <span style={{ ...type.sans, fontSize: 12, color: colors.inkSoft, textAlign: "center" }}>Usiamo il tuo nome solo per identificare l'ordine al tavolo. Informativa privacy</span>
          </button>
        )}
      </div>
    </div>
  );
}

const big = { ...type.serif, fontSize: 34, color: colors.ink };
const subtle = { ...type.sans, fontSize: 16, color: colors.inkSoft, lineHeight: "24px" };
const textInput = { ...type.sans, fontSize: 18, color: colors.ink, background: colors.surface, border: `1px solid ${colors.line}`, borderRadius: radius.md, padding: `0 ${spacing.md}px`, height: 56 };
const cta = { background: colors.accent, height: 56, border: "none", borderRadius: radius.md, color: colors.onAccent, ...type.sansSemi, fontSize: 17, marginTop: spacing.sm };
const errS = { ...type.sansMedium, fontSize: 14, color: colors.danger };
const iconBtn = { background: "none", border: "none", display: "flex", padding: 0 };
